/**
 * Estados vazios da lista da aba Meus Estudos do Flux Personalizado (#2003): o "filtro sem
 * resultado" (estado 3, .dc.html:419-429) e a "empresa sem nenhum estudo" (estado 4,
 * .dc.html:431-441). A lógica do protótipo está em .dc.html:681-687.
 */

import { hasActiveFilters } from 'helper/flux/myStudiesFilters';
import { formatDateOnly } from 'helper/flux/studyDates';
import { DateOnly, MyStudiesPage } from 'types/Flux/Personalized/myStudies';
import {
  MyStudiesDateRef,
  MyStudiesFilters,
  MyStudiesFiltersChange,
  MyStudiesPeriod,
} from 'types/Flux/Personalized/myStudiesFilters';

/** Um degrau da cadeia de períodos (.dc.html:682): título do vazio e o período seguinte. */
interface PeriodStep {
  title: string;
  label: string;
  next: MyStudiesPeriod;
}

/** Hoje → semana → mês → tudo, e intervalo → tudo (.dc.html:682). */
const PERIOD_STEPS: Readonly<Record<Exclude<MyStudiesPeriod, 'tudo'>, PeriodStep>> = {
  hoje: { title: 'Nenhum estudo hoje.', label: 'Ver esta semana', next: 'semana' },
  semana: { title: 'Nenhum estudo nesta semana.', label: 'Ver este mês', next: 'mes' },
  mes: { title: 'Nenhum estudo neste mês.', label: 'Ver todo o período', next: 'tudo' },
  intervalo: { title: 'Nenhum estudo neste intervalo.', label: 'Ver todo o período', next: 'tudo' },
};

/** Título do vazio em "Tudo" (.dc.html:684). */
const NO_PERIOD_TITLE = 'Nenhum estudo com esses filtros.';

/** "Ver todos da empresa": Criado por volta para Todos (.dc.html:686-687). */
const SHOW_ALL_CREATORS: MyStudiesEmptyAction = {
  label: 'Ver todos da empresa',
  change: { criadoPor: 'empresa' },
};

/**
 * Referência no subtítulo (.dc.html:685). Não é o `DATE_REF_LABELS` do `ReferencePopover`: lá a
 * criação é "Criação do estudo", e o subtítulo do design diz só "criação", como o chip.
 */
const DATE_REF_SUBTITLE: Readonly<Record<MyStudiesDateRef, string>> = {
  last_exec: 'última execução',
  created: 'criação',
};

/** Prefixo do subtítulo do estado 3 (D26-2, .dc.html:424). */
const SUBTITLE_PREFIX = 'Filtro: ';

/** Usuário filtrado sem nome conhecido (#2000). */
const UNNAMED_USER = 'usuário selecionado';

/** O que a tela conhece e os filtros não guardam. */
export interface MyStudiesEmptyStateContext {
  /** Papel de quem vê a tela: admin com Todas as empresas lê "Todas as empresas". */
  isAdmin: boolean;
  /**
   * Nome do usuário filtrado em Criado por (`useMyStudiesFilterLabels().userLabel`, #2000). Sem
   * ele, vale o `nome` guardado no filtro e, sem nenhum, "usuário selecionado".
   */
  userLabel?: string;
  /** Hoje em São Paulo (`todayInSaoPaulo(new Date())`): a data do subtítulo em "Hoje". */
  todaySP: DateOnly;
}

/** Ação que amplia a busca: o rótulo do botão e a mudança para o `setFilters`. */
export interface MyStudiesEmptyAction {
  label: string;
  change: MyStudiesFiltersChange;
}

/** Textos e ações do estado 3 ("filtro sem resultado"). */
export interface MyStudiesEmptyState {
  /** Ex.: 'Nenhum estudo hoje.'. */
  title: string;
  /** Ex.: 'Filtro: Meus estudos · última execução em 07/10/2026'. */
  subtitle: string;
  /**
   * Ação principal, que amplia um degrau: o período (hoje → semana → mês → tudo; intervalo →
   * tudo) e, já em Tudo, Criado por para Todos. `null` em Tudo com Todos: aí a principal é
   * "Limpar filtros", sozinha.
   */
  widen: MyStudiesEmptyAction | null;
  /**
   * Mostra "Limpar filtros"? Só quando ele leva a outra lista que não a de `widen`: há filtro ativo
   * nos filtros APLICADOS já ampliados (`hasActiveFilters({ ...filters, ...widen.change })`; sem
   * `widen`, nos aplicados). É o `hasActiveFilters` do helper, e não o do hook, que olha a busca
   * digitada: o vazio é a resposta dos filtros aplicados. Ex.: "Este mês" com Todos e nada mais só
   * mostra "Ver todo o período"; "Hoje" com "Meus estudos" mostra os dois. Com `widen`, é o botão
   * secundário; sem ele, o único.
   */
  canClear: boolean;
}

/**
 * Início do subtítulo (D10, .dc.html:685): quem criou os estudos procurados.
 *
 * Com Todos, admin em Todas as empresas lê "Todas as empresas"; com uma empresa escolhida, lê
 * "Todos da empresa", como o segmento do filtro (.dc.html:659).
 */
function scopeLabel(
  { criadoPor, empresa }: MyStudiesFilters,
  { isAdmin, userLabel }: MyStudiesEmptyStateContext,
): string {
  if (criadoPor === 'me') return 'Meus estudos';
  if (typeof criadoPor === 'object') {
    return `Criado por ${userLabel?.trim() || criadoPor.nome?.trim() || UNNAMED_USER}`;
  }
  return isAdmin && empresa === 'todas' ? 'Todas as empresas' : 'Todos da empresa';
}

/** Fim do subtítulo (.dc.html:685): a referência fora de Tudo e, só em Hoje, a data de hoje. */
function periodLabel({ periodo, ref }: MyStudiesFilters, todaySP: DateOnly): string {
  if (periodo === 'tudo') return '';
  const day = periodo === 'hoje' ? ` em ${formatDateOnly(todaySP)}` : '';
  return ` · ${DATE_REF_SUBTITLE[ref]}${day}`;
}

/**
 * Estado 3, "filtro sem resultado" (.dc.html:681-687): título, subtítulo e ações.
 *
 * - Fora de Tudo, a ação amplia o período um degrau e muda só ele.
 * - Em Tudo com "Meus estudos" ou um usuário: "Nenhum estudo com esses filtros." e "Ver todos da
 *   empresa".
 * - Em Tudo com Todos: o mesmo título, e "Limpar filtros" é o único botão (no protótipo ele
 *   aparecia duas vezes, .dc.html:281-282 e 686-687).
 * - "Limpar filtros" ao lado da ação principal só quando leva a outra lista: se ampliar já não
 *   deixa nenhum filtro ativo, os dois botões dariam na mesma, e fica só a ação principal.
 * - Subtítulo com o prefixo "Filtro: " da D26 (.dc.html:424), quem criou e, fora de Tudo, a
 *   referência; em Hoje, a data de hoje em SP (dd/mm/aaaa). Status, busca e empresa não entram,
 *   como no design.
 *
 * @param filters - Filtros APLICADOS (`useMyStudiesFilters().filters`), os da resposta vazia.
 * @param context - Papel, nome do usuário filtrado e hoje em São Paulo.
 * @returns Os textos e as ações do vazio.
 */
export function buildEmptyState(
  filters: MyStudiesFilters,
  context: MyStudiesEmptyStateContext,
): MyStudiesEmptyState {
  const subtitle = `${SUBTITLE_PREFIX}${scopeLabel(filters, context)}${periodLabel(filters, context.todaySP)}`;
  const step = filters.periodo === 'tudo' ? null : PERIOD_STEPS[filters.periodo];
  let widen: MyStudiesEmptyAction | null = null;
  if (step) widen = { label: step.label, change: { periodo: step.next } };
  else if (filters.criadoPor !== 'empresa') widen = SHOW_ALL_CREATORS;
  // Sem botão repetido: "Limpar filtros" só se ampliar ainda deixar algum filtro ativo
  const canClear = hasActiveFilters(widen ? { ...filters, ...widen.change } : filters);

  return {
    title: step?.title ?? NO_PERIOD_TITLE, subtitle, widen, canClear,
  };
}

/**
 * Estado 4, "empresa sem nenhum estudo" (.dc.html:431-441)? Só quando a página veio vazia E o
 * escopo do usuário não tem nenhum estudo (`escopo_tem_estudos` falso): com estudos no escopo,
 * vale o estado 3, que sugere ampliar.
 *
 * Admin com uma empresa escolhida fica sempre no estado 3: o estado 4 esconde os filtros, e ele
 * não poderia trocar a empresa. Pelo contrato, o escopo ignora a empresa, então isso só pesa se o
 * backend restringir o escopo a ela.
 *
 * @param page - `total` e `scopeHasStudies` da resposta da lista.
 * @param filters - Filtros aplicados (só a empresa importa).
 * @param isAdmin - Papel de quem vê a tela.
 */
export function isCompanyWithoutStudies(
  { total, scopeHasStudies }: Pick<MyStudiesPage, 'total' | 'scopeHasStudies'>,
  { empresa }: Pick<MyStudiesFilters, 'empresa'>,
  isAdmin: boolean,
): boolean {
  if (total !== 0 || scopeHasStudies) return false;
  return !(isAdmin && empresa !== 'todas');
}
